import { Input } from '@mm/components';
import { SearchIcon } from '@mm/icons';
import FBPostCRUDActions from '@mm/state/actions/FBPostCRUDActions';
import PromoterCRUDActions from '@mm/state/actions/PromoterCRUDActions';
import FBPostModel from '@mm/state/model/FBPostModel';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import { activeFacebookPagePlanInfoSelector } from '@mm/state/selectors/facebookPageSelector';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors } from '@mm/utils';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { FormGroup } from 'reactstrap';
import { compose, withStateHandlers } from 'recompose';
import { Dispatch } from 'redux';
import guardModelMapper from '../../../mappers/guardModelMapper';
import CGDropdown from '../../CGDropdown/CGDropdown';
import { POST_NOT_FOUND } from './constants';
import { getPostFromUrl, retrieveAndUpdatePostInfo } from './helpers';
import styles from './SpecifiedPostContainer.module.css';
import { OnPostLinkChange, SetErrorMessage } from './types';

type OuterProps = {
  guard: PromoterModel;
  botId: number;
};

type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromWithState = {
  showDropdown: boolean;
  postLink: string;
  onPostLinkChange: OnPostLinkChange;
  setErrorMessage: SetErrorMessage;
  errorMessage: string;
};

type InnerProps = PropsFromWithState & PropsFromDispatch & PropsFromState;

type OwnProps = OuterProps & PropsFromWithState;
type Props = OuterProps & InnerProps;

const SpecifiedPostContainer = (props: Props) => {
  const {
    guard,
    botId,
    postLink,
    onPostLinkChange,
    showDropdown,
    updateGuard,
    errorMessage,
    connectedPage,
    freeOrSpecialPricePlan,
    retrieveFbPost,
    setErrorMessage,
  } = props;

  const [selectedPost, setSelectedPost] = useState(guard.postInfo);

  const updatePostInfo = async () => {
    if (postLink && !errorMessage) {
      const parsedPostId = getPostFromUrl(postLink);
      if (parsedPostId) {
        const postInfo = await retrieveAndUpdatePostInfo(
          retrieveFbPost,
          updateGuard,
          connectedPage,
          parsedPostId,
          setErrorMessage,
        );

        setSelectedPost(postInfo || {});
        if (postInfo) onPostLinkChange('');
      } else {
        setErrorMessage(POST_NOT_FOUND);
      }
    }
  };

  return (
    <div className={styles.container}>
      <FormGroup>
        <Input
          value={postLink}
          onChange={e => onPostLinkChange(e.target.value)}
          onBlur={updatePostInfo}
          onKeyDown={e => {
            if (e.key === ENTER_KEY_NAME) updatePostInfo();
          }}
          placeholder="Enter post ID or URL"
          icon={<SearchIcon color={colors.darkGray} />}
          css={{ container: styles.inputContainer, input: styles.inputInput }}
        />
      </FormGroup>
      {errorMessage && (
        <FormGroup className="edit-comment-guard-panel__errors">
          {errorMessage}
        </FormGroup>
      )}
      {showDropdown && (
        <CGDropdown
          disabled={freeOrSpecialPricePlan}
          connectedPage={connectedPage}
          selectedPost={selectedPost}
          guard={guard}
          botId={botId}
          updateGuard={updateGuard}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (
  dispatch: Dispatch<StoreState>,
  ownProps: OwnProps,
) => ({
  retrieveFbPost: (fbPageId, postId) => {
    return dispatch(FBPostCRUDActions.retrieveById(fbPageId, postId));
  },
  updateGuard: postInfo => updateCommentGuard(dispatch, postInfo, ownProps),
});

const updateCommentGuard = (
  dispatch: Dispatch<StoreState>,
  postInfo: FBPostModel,
  ownProps: OwnProps,
) => {
  const { guard, botId } = ownProps;

  if (guard && postInfo) {
    const fullPostId = postInfo.promotableId || postInfo.id;
    const postId = fullPostId && fullPostId.split('_')[1];
    const nextGuard = { ...guard, postId, postInfo };
    return dispatch(
      PromoterCRUDActions.update(botId, guardModelMapper(nextGuard, botId)),
    );
  }
};

const mapStateToProps = state => ({
  connectedPage: connectedPageSelector(state),
  freeOrSpecialPricePlan:
    activeFacebookPagePlanInfoSelector(state).info.isFreeOrSpecialPlan,
});

const initialState = {
  showDropdown: true,
  postLink: '',
  errorMessage: '',
};

const onPostLinkChange = () => nextValue => ({
  postLink: nextValue,
  showDropdown: !nextValue,
  errorMessage: '',
});

const setErrorMessage = () => errorMessage => ({ errorMessage });

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, { onPostLinkChange, setErrorMessage }),
);

export default enhance(SpecifiedPostContainer);
